import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DayEvents, EventRow, MonthGrid, PlatformLegend, monthName } from "../components/Calendar";
import { StartMeeting } from "../components/StartMeeting";
import { endpoints, type Me, type Meeting } from "../lib/api";
import {
  addDays,
  meetingPlatformKey,
  sameDay,
  startOfDay,
  startOfWeek,
  useCalendarEvents,
  PLATFORM_NAMES,
} from "../lib/calendar";
import { duration, shortDate, upcomingTime } from "../lib/format";

/** Recent meetings shown here; the rest are on the Meetings page. */
const RECENT_LIMIT = 10;
const RANGES = { day: 1, week: 7, month: 30 } as const;
type Range = keyof typeof RANGES;

/** Home: a welcome with today's numbers, a box to send a bot, what's live, a
 * month of meetings, and the recent ones. */
export function Dashboard({ me }: { me: Me | null }) {
  const queryClient = useQueryClient();
  const meetings = useQuery({ queryKey: ["meetings"], queryFn: endpoints.meetings, refetchInterval: 5_000 });
  const { events } = useCalendarEvents();
  const [month, setMonth] = useState(() => startOfDay(new Date()));
  const [picked, setPicked] = useState<Date>(() => startOfDay(new Date()));
  const [range, setRange] = useState<Range>("week");

  const stop = useMutation({
    mutationFn: (sessionId: string) => endpoints.stopMeeting(sessionId, me?.csrf_token ?? ""),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["meetings"] }),
  });

  const all = meetings.data ?? [];
  const live = all.filter((m) => m.active);
  const now = new Date();
  const today = startOfDay(now);
  const weekEnd = addDays(startOfWeek(now), 7);
  const upcoming = events.filter((e) => e.upcoming && e.start > now);
  const scheduledToday = upcoming.filter((e) => sameDay(e.start, today)).length;
  const scheduledWeek = upcoming.filter((e) => e.start < weekEnd).length;
  const since = addDays(today, 1 - RANGES[range]);
  const recent = all
    .filter((m) => !m.active && new Date(m.starts_at ?? m.requested_at ?? 0) >= since)
    .slice(0, RECENT_LIMIT);
  const next = upcoming.find((e) => e.state !== "cancelled");

  return (
    <div className="page-body home">
      <header className="welcome">
        <h1>Welcome, {firstName(me)}</h1>
        <dl className="welcome-stats">
          <div>
            <dt>Recording</dt>
            <dd>
              {live.some((m) => m.state === "recording") && <span className="live-dot" aria-hidden />}
              {live.length}
            </dd>
          </div>
          <div>
            <dt>Scheduled today</dt>
            <dd>{scheduledToday}</dd>
          </div>
          <div>
            <dt>This week</dt>
            <dd>{scheduledWeek}</dd>
          </div>
          <div>
            <dt>Next meeting</dt>
            <dd className="small">{next ? upcomingTime(next.start.toISOString()) : "—"}</dd>
          </div>
        </dl>
      </header>

      <div className="home-grid">
        <div className="home-left">
          <StartMeeting me={me} />

          <section className="card live-card">
            <h2 className="card-title">
              <span className={`dash-square${live.length ? " live" : ""}`} aria-hidden />
              Happening now
            </h2>
            {meetings.error && <div className="error">{(meetings.error as Error).message}</div>}
            {stop.error && <div className="error">{(stop.error as Error).message}</div>}
            {live.length === 0 ? (
              <div className="live-empty">
                <p>No bot is in a meeting right now.</p>
                {next && (
                  <p className="live-next">
                    Next: <strong>{next.title}</strong> · {upcomingTime(next.start.toISOString())}
                    {next.platform && ` · ${PLATFORM_NAMES[next.platform]}`}
                  </p>
                )}
              </div>
            ) : (
              <ul className="live-list">
                {live.map((meeting) => (
                  <LiveRow key={meeting.session_id} meeting={meeting} stopping={stop.isPending}
                           onStop={() => stop.mutate(meeting.session_id!)} />
                ))}
              </ul>
            )}
          </section>

          <section className="card upnext-card">
            <div className="cal-head">
              <h2 className="card-title">Up next</h2>
              <Link className="btn-text" to="/calendar">Open calendar →</Link>
            </div>
            {upcoming.length === 0 ? (
              <p className="day-events-empty">Nothing on your calendar with a Meet, Teams or Zoom link.</p>
            ) : (
              <ul className="upnext">
                {upcoming.slice(0, 5).map((event) => (
                  <EventRow key={event.key} event={event} withDay />
                ))}
              </ul>
            )}
          </section>
        </div>

        <section className="card cal-card">
          <div className="cal-head">
            <h2 className="card-title">{monthName(month)}</h2>
            <div className="cal-nav">
              <button className="btn-quiet" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label="Previous month">‹</button>
              <button className="btn-quiet" onClick={() => { setMonth(today); setPicked(today); }}>Today</button>
              <button className="btn-quiet" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label="Next month">›</button>
            </div>
          </div>
          <MonthGrid events={events} month={month} selected={picked} onSelect={setPicked} />
          <PlatformLegend />
          <DayEvents day={picked} events={events} />
        </section>
      </div>

      <section className="card recent-card">
        <div className="recent-head">
          <h2 className="card-title">Recent meetings</h2>
          <div className="segmented" role="tablist" aria-label="How far back">
            {(Object.keys(RANGES) as Range[]).map((key) => (
              <button key={key} role="tab" aria-selected={range === key} className={range === key ? "on" : ""}
                      onClick={() => setRange(key)}>
                {{ day: "Today", week: "Week", month: "Month" }[key]}
              </button>
            ))}
          </div>
        </div>
        {recent.length === 0 ? (
          <p className="dash-empty">{meetings.isLoading ? "Loading…" : `No meetings finished ${range === "day" ? "today" : `in the last ${range}`}.`}</p>
        ) : (
          <div className="dash-table" role="table" aria-label="Recent meetings">
            <div className="dash-thead" role="row">
              <span role="columnheader">Meeting</span>
              <span role="columnheader">Date</span>
              <span role="columnheader">Duration</span>
              <span role="columnheader">Transcript</span>
              <span aria-hidden />
            </div>
            {recent.map((meeting) => (
              <FinishedRow key={meeting.session_id} meeting={meeting} />
            ))}
          </div>
        )}
        <Link className="btn-secondary show-all" to="/meetings">Show all meetings</Link>
      </section>
    </div>
  );
}

function LiveRow({ meeting, stopping, onStop }: { meeting: Meeting; stopping: boolean; onStop: () => void }) {
  const key = meetingPlatformKey(meeting);
  return (
    <li className={`live-row p-${key ?? "none"}`}>
      <div className="dash-main">
        <p className="dash-title">
          {meeting.title ?? meeting.meet_url}
          {key ? <span className="dash-platform">{PLATFORM_NAMES[key]}</span>
            : meeting.in_person && <span className="dash-platform">In person</span>}
        </p>
        <p className="dash-meta">
          <span className={`status ${meeting.state}`}>
            <span className="status-dot" aria-hidden />
            {meeting.label}
          </span>
          {meeting.owner_email && <span>by {meeting.owner_email}</span>}
        </p>
      </div>
      {meeting.can_stop && meeting.session_id && (
        <button className="btn-quiet danger" disabled={stopping} onClick={onStop}>
          {meeting.state === "scheduled" || meeting.state === "waiting" ? "Cancel" : "Stop"}
        </button>
      )}
    </li>
  );
}

/** One finished meeting: a whole-row link to its page. */
function FinishedRow({ meeting }: { meeting: Meeting }) {
  const key = meetingPlatformKey(meeting);
  const cells = (
    <>
      <span className="dash-cell-main">
        <span className="dash-title">{meeting.title ?? meeting.meet_url ?? meeting.session_id}</span>
        {key ? <span className={`dash-platform stacked p-${key}`}>{PLATFORM_NAMES[key]}</span>
          : meeting.in_person && <span className="dash-platform stacked">In person</span>}
      </span>
      <span className="dash-num">{shortDate(meeting.starts_at ?? meeting.requested_at)}</span>
      <span className="dash-num">{meeting.duration_seconds != null ? duration(meeting.duration_seconds) : "—"}</span>
      <Outcome meeting={meeting} />
      <span className="dash-arrow" aria-hidden>→</span>
    </>
  );
  return meeting.session_id ? (
    <Link className="dash-row" role="row" to={`/meetings/${meeting.session_id}`}>{cells}</Link>
  ) : (
    <div className="dash-row" role="row">{cells}</div>
  );
}

/** Ready or on its way when there's a recording; otherwise why there isn't. */
function Outcome({ meeting }: { meeting: Meeting }) {
  if (meeting.state === "failed" || meeting.state === "not_admitted") {
    return <span className="status failed">{meeting.label}</span>;
  }
  if (meeting.transcript === "ready") return <span className="status ready">Ready</span>;
  if (meeting.transcript === "pending") {
    return (
      <span className="status waiting">
        <span className="status-dot" aria-hidden />
        Transcribing…
      </span>
    );
  }
  return <span className="status">{meeting.state === "cancelled" ? "Cancelled" : "No transcript"}</span>;
}

function firstName(me: Me | null): string {
  if (!me) return "";
  if (me.name) return me.name.split(" ")[0];
  const local = me.email.split("@")[0];
  return local.charAt(0).toUpperCase() + local.slice(1);
}
